import { useSelector } from "react-redux";
import { RootState } from "@/store/reducers";
import {
  DateField,
  InputField,
  SelectField,
  TimeField,
  UploadImageField,
} from "@/components/Form";
import { Button, Image, ImageList } from "@/components/Elements";
import { AIInput, SearchPinCode } from "@/features/misc/components";
import { mapLocationToString, useCustomForm } from "@/hooks";
import { FileModel } from "@/types/common";
import { addEventSchema, AddEventSchemaModel } from "../types/schema";
import { useAddEditEventMutation, useAddEventViewQuery } from "../api/event";
import { useLocation, useNavigate } from "react-router-dom";
import { RouteConstant } from "@/routes/routeConstant";
import { useEffect, useMemo } from "react";
import { convertToDate } from "@/libs/dateHelper";
import { PostStatusOption } from "@/utils/listOption";

export const AddEventScreen = () => {
  const { register, formState, setValue, handleSubmit, watch, reset } =
    useCustomForm<AddEventSchemaModel>({ schema: addEventSchema });
  const {
    startDate,
    startTime,
    endDate,
    endTime,
    location,
    files,
    eventTypeId,
  } = watch();
  const distanceRadius = useSelector(
    (state: RootState) => state.login.userDetail.distanceRadius
  );
  const { data } = useAddEventViewQuery();
  const { eventTypes, timezones } = data ?? { eventTypes: [], timezones: [] };
  const [addEditEvent] = useAddEditEventMutation();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const selectedEvent = routeLocation.state?.selectedEvent;

  useEffect(() => {
    if (selectedEvent)
      reset({
        ...selectedEvent,
        startTime: convertToDate(selectedEvent.startDate),
        endTime: convertToDate(selectedEvent.endDate),
      });
  }, [selectedEvent]);

  useEffect(() => {
    setValue("eventType", "");
  }, [eventTypeId]);

  const selectedEventType = useMemo(() => {
    if (eventTypes && eventTypeId) {
      return eventTypes.find((c) => parseInt(c.value) == eventTypeId)?.text;
    }

    return "";
  }, [eventTypes, eventTypeId]);

  const handleUploadImg = (name: string, value: FileModel[]) => {
    setValue("files", [...(files ? files : []), ...value]);
  };

  async function handleDraft(payload: any) {
    handleSave({ ...payload, isDraft: true });
  }

  async function handleSave({ location, isDraft, ...rest }: any) {
    await addEditEvent({
      ...rest,
      postLocation: mapLocationToString(location),
      postStatus: isDraft ? PostStatusOption.Draft : PostStatusOption.Active,
    }).unwrap();
    reset();
    navigate(RouteConstant.Events);
  }

  return (
    <>
      <div className="p-0 mt-[10px] pb-[50px]">
        <InputField
          type="text"
          placeholder="Title"
          registration={register("title")}
          className="text-[20px]"
        />
        <SelectField
          registration={register("eventTypeId")}
          defaultValue={selectedEvent?.eventTypeId}
          className="text-[20px]"
          label="Event Type"
          options={eventTypes}
          wrapperClassName="mt-[10px]"
        />
        {selectedEventType == "Other" && (
          <InputField
            type="text"
            placeholder="Other Event Type"
            registration={register("eventType")}
            className="text-[20px]"
          />
        )}
        <div className="maz-h-[45%] mt-[10px]">
          <InputField
            type="text"
            placeholder="Write something here..."
            registration={register("description")}
            error={formState.errors["description"]}
            className="text-[20px]"
          />
        </div>
        <AIInput setValue={setValue} />
        <div className="p-[17px] border-b border-b-[#d3d3d3]">
          <UploadImageField name="file" setValue={handleUploadImg}>
            <div className="flex">
              <Image src="add-image.png" className="w-[25px] h-[25px]" />
              <div className="text-[15px] ml-4 text-center">
                Add Image/Banner
              </div>
            </div>
          </UploadImageField>
          {files && (
            <ImageList
              files={files}
              handleChange={(value: FileModel[]) => setValue("files", value)}
              showRemove
            />
          )}
        </div>
        <div className="flex justify-between mt-[10px]">
          <div className="w-[49%]">
            <DateField
              name="startDate"
              value={startDate}
              setValue={setValue}
              label="Start Date"
            />
          </div>
          <div className="w-[49%]">
            <TimeField
              name="startTime"
              value={startTime}
              setValue={setValue}
              label="Start Time"
            />
          </div>
        </div>
        <div className="flex justify-between mt-[10px]">
          <div className="w-[49%]">
            <DateField
              name="endDate"
              value={endDate}
              setValue={setValue}
              label="Emd Date"
            />
          </div>
          <div className="w-[49%]">
            <TimeField
              name="endTime"
              value={endTime}
              setValue={setValue}
              label="End Time"
            />
          </div>
        </div>
        <div className="mt-[10px]">
          <SelectField
            registration={register("timezoneId")}
            defaultValue={selectedEvent?.timezoneId}
            className="text-[20px]"
            label="Timezone"
            options={timezones}
          />
        </div>
        <div className="p-[17px] border-b border-b-[#d3d3d3]">
          <SearchPinCode
            location={location}
            onChange={setValue}
            radius={distanceRadius}
          >
            <Image src="add-location.png" className="w-[25px] h-[25px]" />
            <div className="text-[15px] ml-4 text-center">Add Location</div>
          </SearchPinCode>
        </div>
      </div>
      <div className="flex justify-between absolute bottom-3 z-10 bg-[#ffff] right-3 left-3">
        <Button onClick={handleSubmit(handleDraft)} className="w-[48%]">
          Save as draft
        </Button>
        <Button onClick={handleSubmit(handleSave)} className="w-[48%]">
          Publish
        </Button>
      </div>
    </>
  );
};
